/**
 * Config-driven endpoint paths, relative to `apiConfig.apiBaseUrl`. Kept in
 * one place so a backend route rename requires exactly one edit here.
 */
export const endpoints = {
  auth: {
    // Matches the real backend contract exactly (backend/src/api/routes/
    // auth.py) - there is no `/auth/session` endpoint; it never existed
    // on the backend and was removed here as dead/obsolete (Sprint:
    // React Authentication Foundation).
    login: "/auth/login",
    refresh: "/auth/refresh",
    logout: "/auth/logout",
  },
  dashboard: {
    summary: "/dashboard",
    system: "/dashboard/system",
    trading: "/dashboard/trading",
    portfolio: "/dashboard/portfolio",
  },
  portfolio: {
    // Backend v2.1 (Sprint 4.2) Portfolio Read Model - each reads
    // whatever `PortfolioPipeline` has already persisted for a
    // `portfolio_id`, zero fabricated input. `snapshot` is the one write
    // path (moved here unchanged from the former `GET /portfolio`).
    root: "/portfolio",
    summary: "/portfolio/summary",
    holdings: "/portfolio/holdings",
    positions: "/portfolio/positions",
    snapshot: "/portfolio/snapshot",
  },
  trading: {
    paper: "/trading/paper",
    replay: "/trading/replay",
  },
  analytics: {
    // Backend v2.1 (Sprint 4.3) Analytics Read Model - structured DTOs
    // over the persisted `AnalyticsSnapshot`, zero fabricated
    // `equity_curve` input. `generate` is the one write path (moved here
    // unchanged from the former `GET /analytics`).
    root: "/analytics",
    performance: "/analytics/performance",
    generate: "/analytics/generate",
  },
  health: {
    // Backend mounts `health.router` with no prefix (root-level
    // `/health/live` and `/health/ready`, outside `/api/v1`). The
    // `/api/v1/system/{liveness,readiness}` aliases (system.py, ADR-036)
    // return the identical, proven-equal payload and stay reachable
    // through the same `apiBaseUrl`-relative client every other service
    // uses - so this service targets those aliases instead of the
    // unversioned root paths.
    live: "/system/liveness",
    ready: "/system/readiness",
  },
  system: {
    startup: "/system/startup",
    readiness: "/system/readiness",
    liveness: "/system/liveness",
    version: "/system/version",
    build: "/system/build",
  },
  sync: {
    root: "/sync",
    orders: "/sync/orders",
    trades: "/sync/trades",
    positions: "/sync/positions",
    holdings: "/sync/holdings",
    status: "/sync/status",
  },
  brokers: {
    root: "/brokers",
    status: "/brokers/status",
    capabilities: "/brokers/capabilities",
    detail: (providerId: string) => `/brokers/${providerId}`,
  },
  certification: {
    status: "/certification/status",
    report: "/certification/report",
  },
  executions: {
    // Falcon's own recorded order/trade history (Execution's
    // InMemoryExecutionResultRepository, ADR-032) - read-only, zero
    // arguments. Also the Orders/Trades workspace's own data source
    // (unrelated to Portfolio, which now reads its own Read Model below).
    orderBook: "/orders",
    tradeBook: "/trades",
    orderDetail: (executionId: string) => `/orders/${executionId}`,
    // Backend v2.1 (Sprint 4.5) pre-trade endpoints - both strictly
    // read-only: `preview` runs the real Risk Engine without submitting;
    // `estimate` reads whatever order-economics values already exist.
    // Neither ever creates an order.
    preview: "/orders/preview",
    estimate: "/orders/estimate",
  },
  reports: {
    // Backend v2.1 (Sprint 4.3) Reporting Read Model - `root` lists every
    // already-persisted report's metadata for a `portfolio_id`, `byName`
    // reads one report type's metadata. `generate` is the one write path
    // (moved here unchanged from the former `GET /reports`); only the
    // Portfolio Report use case is wired to it (TD-099).
    root: "/reports",
    byName: (reportName: string) => `/reports/${reportName}`,
    generate: "/reports/generate",
  },
  marketData: {
    // `backend/src/api/routes/market_data.py` (ADR-031) - unlike
    // `portfolio.root`/`reports.root`, these genuinely accept
    // zero-argument or user-selected (not fabricated) inputs: `instruments`
    // takes optional search filters, `quote`/`history`/`instrument` take
    // an instrument_token the user actually selected via search.
    quote: "/market-data/quote",
    history: "/market-data/history",
    instruments: "/market-data/instruments",
    instrument: "/market-data/instrument",
    providers: "/market-data/providers",
  },
} as const;
